/**
 * Comprehensive worldwide timezone abbreviation → IANA map.
 * Where an abbreviation is ambiguous (e.g. IST = India / Israel / Ireland),
 * the most-populated / most-common interpretation is used.
 */
const ABBR_TO_IANA: Record<string, string> = {
  // UTC / Universal
  utc: 'UTC',
  gmt: 'Etc/GMT',
  z: 'UTC',
  // Americas — North
  hst: 'Pacific/Honolulu',
  hdt: 'Pacific/Honolulu',
  akst: 'America/Anchorage',
  akdt: 'America/Anchorage',
  pst: 'America/Los_Angeles',
  pdt: 'America/Los_Angeles',
  mst: 'America/Denver',
  mdt: 'America/Denver',
  cst: 'America/Chicago',
  cdt: 'America/Chicago',
  est: 'America/New_York',
  edt: 'America/New_York',
  ast: 'America/Halifax',
  adt: 'America/Halifax',
  nst: 'America/St_Johns',
  ndt: 'America/St_Johns',
  // Americas — Central & South
  cot: 'America/Bogota',
  ect: 'America/Guayaquil',
  pet: 'America/Lima',
  vet: 'America/Caracas',
  bot: 'America/La_Paz',
  art: 'America/Argentina/Buenos_Aires',
  brt: 'America/Sao_Paulo',
  brst: 'America/Sao_Paulo',
  gft: 'America/Cayenne',
  srt: 'America/Paramaribo',
  uyt: 'America/Montevideo',
  pyt: 'America/Asuncion',
  clt: 'America/Santiago',
  clst: 'America/Santiago',
  // Atlantic
  cvt: 'Atlantic/Cape_Verde',
  azot: 'Atlantic/Azores',
  azost: 'Atlantic/Azores',
  // Europe
  wet: 'Europe/Lisbon',
  west: 'Europe/Lisbon',
  bst: 'Europe/London',
  cet: 'Europe/Paris',
  cest: 'Europe/Paris',
  eet: 'Europe/Helsinki',
  eest: 'Europe/Helsinki',
  msk: 'Europe/Moscow',
  fet: 'Europe/Kaliningrad',
  trt: 'Europe/Istanbul',
  samt: 'Europe/Samara',
  // Africa
  wat: 'Africa/Lagos',
  cat: 'Africa/Harare',
  eat: 'Africa/Nairobi',
  sast: 'Africa/Johannesburg',
  // Middle East
  irst: 'Asia/Tehran',
  irdt: 'Asia/Tehran',
  idt: 'Asia/Jerusalem',
  gst: 'Asia/Dubai',
  // Asia
  ist: 'Asia/Kolkata',
  npt: 'Asia/Kathmandu',
  bdt: 'Asia/Dhaka',
  slt: 'Asia/Colombo',
  mmt: 'Asia/Rangoon',
  ict: 'Asia/Bangkok',
  wib: 'Asia/Jakarta',
  wita: 'Asia/Makassar',
  wit: 'Asia/Jayapura',
  hkt: 'Asia/Hong_Kong',
  sgt: 'Asia/Singapore',
  myt: 'Asia/Kuala_Lumpur',
  pht: 'Asia/Manila',
  jst: 'Asia/Tokyo',
  kst: 'Asia/Seoul',
  tlt: 'Asia/Dili',
  pgt: 'Pacific/Port_Moresby',
  yekt: 'Asia/Yekaterinburg',
  omst: 'Asia/Omsk',
  krat: 'Asia/Krasnoyarsk',
  irkt: 'Asia/Irkutsk',
  yakt: 'Asia/Yakutsk',
  vlat: 'Asia/Vladivostok',
  magt: 'Asia/Magadan',
  sakt: 'Asia/Sakhalin',
  pett: 'Asia/Kamchatka',
  azt: 'Asia/Baku',
  azst: 'Asia/Baku',
  get: 'Asia/Tbilisi',
  amt: 'Asia/Yerevan',
  kgt: 'Asia/Bishkek',
  tmt: 'Asia/Ashgabat',
  uzt: 'Asia/Tashkent',
  tjt: 'Asia/Dushanbe',
  pkt: 'Asia/Karachi',
  aft: 'Asia/Kabul',
  // Oceania
  aest: 'Australia/Sydney',
  aedt: 'Australia/Sydney',
  acst: 'Australia/Darwin',
  acdt: 'Australia/Adelaide',
  awst: 'Australia/Perth',
  nzst: 'Pacific/Auckland',
  nzdt: 'Pacific/Auckland',
  fjt: 'Pacific/Fiji',
  fjs: 'Pacific/Fiji',
  tot: 'Pacific/Tongatapu',
  tost: 'Pacific/Tongatapu',
  wst: 'Pacific/Apia',
  sst: 'Pacific/Pago_Pago',
  chst: 'Pacific/Guam',
  pont: 'Pacific/Pohnpei',
  mht: 'Pacific/Majuro',
  gilt: 'Pacific/Tarawa',
};

/**
 * Parses a timezone string into either:
 * - { type: 'iana'; key: string }   — a valid IANA key usable by Intl (includes Etc/GMT±N)
 * - { type: 'offset'; minutes: number } — a raw UTC-offset in minutes (for fractional offsets
 *   like GMT+5:30 that Intl can't handle directly)
 * - null if unparseable
 *
 * Handles all common formats:
 *   IANA              "America/New_York"
 *   GMT/UTC offset    "GMT-4", "UTC+5", "GMT+5:30", "+05:30", "-04:00"
 *   Windows name      "Eastern Standard Time"
 *   Abbreviation      "EST", "PDT"
 *   UTC text          "(UTC-05:00) Eastern Time (US & Canada)"
 *
 * The POSIX / Etc/GMT sign convention is the *inverse* of the common GMT±N notation:
 *   GMT-4  (UTC-4)  →  Etc/GMT+4
 *   GMT+5  (UTC+5)  →  Etc/GMT-5
 */
function parseTimezone(
  timezone: string,
): { type: 'iana'; key: string } | { type: 'offset'; minutes: number } | null {
  // 1. Try it as a plain IANA identifier first (e.g. "America/New_York", "UTC", "Etc/GMT+4")
  try {
    Intl.DateTimeFormat(undefined, { timeZone: timezone });
    return { type: 'iana', key: timezone };
  } catch {
    // not a valid IANA identifier — fall through
  }

  const lower = timezone.toLowerCase().trim();

  // 3. Timezone abbreviation — comprehensive map
  const fromAbbr = ABBR_TO_IANA[lower];
  if (fromAbbr) return { type: 'iana', key: fromAbbr };

  // 5. Bare "(UTC±HH:MM)" or "(UTC±H)" prefix e.g. "(UTC-4)" or "(UTC+05:30)"
  const utcPrefix = timezone.match(/^\(UTC([+-])(\d{1,2})(?::(\d{2}))?\)/i);
  if (utcPrefix) {
    const sign = utcPrefix[1] === '+' ? 1 : -1;
    const hours = parseInt(utcPrefix[2], 10);
    const mins = parseInt(utcPrefix[3] ?? '0', 10);
    const totalMinutes = sign * (hours * 60 + mins);
    if (mins === 0 && hours <= 14) {
      const etcKey = `Etc/GMT${sign > 0 ? '-' : '+'}${hours}`;
      try {
        Intl.DateTimeFormat(undefined, { timeZone: etcKey });
        return { type: 'iana', key: etcKey };
      } catch {
        /* fall through */
      }
    }
    return { type: 'offset', minutes: totalMinutes };
  }

  // 7. Parse raw offset strings: "GMT-4", "UTC+5", "GMT+5:30", "+05:30", "-04:00", "GMT-04:00"
  const m = timezone.match(/^(?:GMT|UTC)?([+-])(\d{1,2})(?::(\d{2}))?$/i);
  if (m) {
    const sign = m[1] === '+' ? 1 : -1;
    const hours = parseInt(m[2], 10);
    const mins = parseInt(m[3] ?? '0', 10);
    const totalMinutes = sign * (hours * 60 + mins);

    // Whole-hour offsets within ±14 h can be expressed as Etc/GMT±N (sign flipped!)
    if (mins === 0 && hours <= 14) {
      // sign for Etc/ is the *opposite* of the GMT±N sign
      const etcKey = `Etc/GMT${sign > 0 ? '-' : '+'}${hours}`;
      try {
        Intl.DateTimeFormat(undefined, { timeZone: etcKey });
        return { type: 'iana', key: etcKey };
      } catch {
        // shouldn't happen for valid ±14 h, but fall through just in case
      }
    }

    // Fractional offsets (e.g. +05:30) — must be handled by manually shifting the date
    return { type: 'offset', minutes: totalMinutes };
  }

  return null;
}

/**
 * Shifts an ISO date string by `offsetMinutes` and returns a new ISO string
 * that, when interpreted as UTC (timeZone: 'UTC'), shows the correct local time.
 */
function shiftByOffset(iso: string, offsetMinutes: number): string {
  const ms = new Date(iso).getTime() + offsetMinutes * 60_000;
  return new Date(ms).toISOString();
}

/** Zone used to read "as picked" event times back out of their UTC-stamped ISO strings. */
const UTC_ZONE = { type: 'iana', key: 'UTC' } as const;

/** Returns the timezone string only if it's a valid IANA identifier, otherwise undefined. */
export function resolveTimezone(timezone?: string): string | undefined {
  if (!timezone) return undefined;
  const parsed = parseTimezone(timezone);
  return parsed?.type === 'iana' ? parsed.key : undefined;
}

/** Formats an ISO date string as e.g. "Wed, May 13, 2026", respecting the event's timezone. */
export function formatDate(iso?: string, timezone?: string, wallClock = false): string {
  if (!iso) return '—';

  const parsed = wallClock ? UTC_ZONE : timezone ? parseTimezone(timezone) : null;

  if (parsed?.type === 'iana') {
    return new Intl.DateTimeFormat('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      timeZone: parsed.key,
    }).format(new Date(iso));
  }

  if (parsed?.type === 'offset') {
    return new Intl.DateTimeFormat('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      timeZone: 'UTC',
    }).format(new Date(shiftByOffset(iso, parsed.minutes)));
  }

  // No timezone — use browser local time
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(new Date(iso));
}

/** Formats an ISO date string as a time e.g. "6:00 AM (GMT-4)". */
export function formatTime(
  iso?: string,
  timezone?: string,
  wallClock = false,
  labelOverride?: string | null,
): string {
  if (!iso) return '—';

  const parsed = wallClock ? UTC_ZONE : timezone ? parseTimezone(timezone) : null;
  const shownLabel = labelOverride ?? timezone;
  const tzLabel = shownLabel ? ` (${shownLabel})` : '';

  if (parsed?.type === 'iana') {
    const formatted = new Intl.DateTimeFormat('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone: parsed.key,
    }).format(new Date(iso));
    return `${formatted}${tzLabel}`;
  }

  if (parsed?.type === 'offset') {
    const formatted = new Intl.DateTimeFormat('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone: 'UTC',
    }).format(new Date(shiftByOffset(iso, parsed.minutes)));
    return `${formatted}${tzLabel}`;
  }

  return new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(new Date(iso));
}

/** Formats an ISO date string as a bare time e.g. "6:00 AM", with no timezone suffix. */
export function formatTimeOnly(iso?: string, timezone?: string, wallClock = false): string {
  return formatTime(iso, timezone, wallClock).replace(/\s*\([^)]*\)\s*$/, '');
}

/** Returns a UTC offset label like "UTC+1:00" or "UTC-5:00", respecting DST at the given instant. */
export function formatUtcOffset(iso?: string, timezone?: string): string {
  if (!timezone) return '';
  const parsed = parseTimezone(timezone);
  if (!parsed) return '';

  const instant = iso ? new Date(iso) : new Date();
  const offsetMs =
    parsed.type === 'iana' ? getTimezoneOffsetMs(instant, parsed.key) : parsed.minutes * 60000;

  const totalMinutes = Math.round(offsetMs / 60000);
  const sign = totalMinutes >= 0 ? '+' : '-';
  const abs = Math.abs(totalMinutes);
  const hours = Math.floor(abs / 60);
  const minutes = abs % 60;
  return `UTC${sign}${hours}:${String(minutes).padStart(2, '0')}`;
}

/**
 * Returns a human-readable timezone name e.g. "West Africa Time (UTC+1:00)"
 * for an IANA zone, falling back to just the UTC offset when no long name
 * is available (e.g. for raw offset-based timezones).
 */
export function formatTimezoneName(iso?: string, timezone?: string): string {
  if (!timezone) return '';
  const parsed = parseTimezone(timezone);
  if (!parsed || parsed.type !== 'iana') return formatUtcOffset(iso, timezone);

  const instant = iso ? new Date(iso) : new Date();
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: parsed.key,
      timeZoneName: 'long',
    }).formatToParts(instant);
    const longName = parts.find((p) => p.type === 'timeZoneName')?.value;
    if (longName && !/^GMT/.test(longName)) {
      return `${longName} (${formatUtcOffset(iso, timezone)})`;
    }
  } catch {
    // unsupported zone for long-name formatting — fall through to offset-only label
  }
  return formatUtcOffset(iso, timezone);
}

/**
 * Returns the UTC offset (in ms) of `timeZone` at the given instant.
 * Formats the instant's wall-clock time in the target zone, then diffs
 * that (treated as if it were UTC) against the original instant.
 */
function getTimezoneOffsetMs(instant: Date, timeZone: string): number {
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
  const parts: Record<string, string> = {};
  for (const part of dtf.formatToParts(instant)) {
    if (part.type !== 'literal') parts[part.type] = part.value;
  }
  const asUTC = Date.UTC(
    Number(parts.year),
    Number(parts.month) - 1,
    Number(parts.day),
    Number(parts.hour),
    Number(parts.minute),
    Number(parts.second),
  );
  return asUTC - instant.getTime();
}

/**
 * Combines a wall-clock date + time entered in a specific IANA timezone into
 * the correct UTC ISO instant — the counterpart Event pages are missing
 * (their `new Date(`${date}T${time}`).toISOString()` silently uses the
 * browser's local zone instead of the selected one). Uses the standard
 * double-iteration offset technique so DST transitions and fractional-hour
 * zones (e.g. Asia/Kolkata, UTC+5:30) resolve correctly.
 */
export function combineDateTimeInZone(dateStr: string, timeStr: string, timeZone: string): string {
  const naiveUtc = new Date(`${dateStr}T${timeStr}:00Z`);
  const offset1 = getTimezoneOffsetMs(naiveUtc, timeZone);
  let utcMs = naiveUtc.getTime() - offset1;
  // Re-check near DST transitions: the offset at the real instant can differ
  // from the offset at the naive guess.
  const offset2 = getTimezoneOffsetMs(new Date(utcMs), timeZone);
  if (offset2 !== offset1) {
    utcMs = naiveUtc.getTime() - offset2;
  }
  return new Date(utcMs).toISOString();
}

/**
 * Wall-clock date + time in `timeZone`, serialised with that zone's UTC offset
 * (e.g. ("2026-10-10", "18:00", "Africa/Lagos") → "2026-10-10T18:00:00+01:00").
 * The string pins the exact instant while keeping the picked wall clock readable,
 * so it stays correct whether the server keeps aware datetimes as sent or
 * re-applies the separate timezone field to the local part.
 */
export function zonedDateTimeIso(dateStr: string, timeStr: string, timeZone?: string): string {
  const time = timeStr.slice(0, 5);
  const key = resolveTimezone(timeZone);
  if (!key) return new Date(`${dateStr}T${time}:00Z`).toISOString();
  const instant = new Date(combineDateTimeInZone(dateStr, time, key));
  const totalMinutes = Math.round(getTimezoneOffsetMs(instant, key) / 60_000);
  const sign = totalMinutes >= 0 ? '+' : '-';
  const abs = Math.abs(totalMinutes);
  const hh = String(Math.floor(abs / 60)).padStart(2, '0');
  const mm = String(abs % 60).padStart(2, '0');
  return `${dateStr}T${time}:00${sign}${hh}:${mm}`;
}

/** Today's calendar date (YYYY-MM-DD) in `timeZone`, falling back to the browser's zone. */
export function todayInZone(timeZone?: string, now: Date = new Date()): string {
  const key = resolveTimezone(timeZone);
  const parts: Record<string, string> = {};
  for (const part of new Intl.DateTimeFormat('en-US', {
    ...(key ? { timeZone: key } : {}),
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(now)) {
    if (part.type !== 'literal') parts[part.type] = part.value;
  }
  return `${parts.year}-${parts.month}-${parts.day}`;
}

/**
 * Event times are stored "as picked": the wall-clock date/time the user enters
 * is sent as-is with a UTC marker, with no timezone conversion. The selected
 * timezone travels separately as `iana_timezone` and is only a label.
 * e.g. ("2026-06-29", "18:00") → "2026-06-29T18:00:00.000Z"
 */
export function wallClockToUtcIso(dateStr: string, timeStr: string): string {
  return new Date(`${dateStr}T${timeStr.slice(0, 5)}:00Z`).toISOString();
}

/**
 * Wall-clock date/time exactly as picked, with no timezone marker or conversion.
 * e.g. ("2026-06-29", "18:00") → "2026-06-29T18:00:00"
 */
export function wallClockAsPicked(dateStr: string, timeStr: string): string {
  return `${dateStr}T${timeStr.slice(0, 5)}:00`;
}

/** The user's current local wall-clock time in the same zone-less format, e.g. "2026-10-15T12:17:00". */
export function nowAsPicked(now: Date = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}:00`;
}

/** Counterpart to `wallClockToUtcIso`: reads the stored UTC fields back as the picked date/time. */
export function wallClockFromUtcIso(iso: string): { date: string; time: string } {
  return { date: iso.slice(0, 10), time: iso.slice(11, 16) };
}

/**
 * "Now" expressed on the same scale as "as picked" times: the current wall-clock
 * in `timeZone`, as epoch ms with a UTC stamp. Compare this against a stored
 * time to know whether it has started. Falls back to real now without a valid zone.
 */
export function nowAsWallClockMs(timeZone?: string, now: Date = new Date()): number {
  const key = resolveTimezone(timeZone);
  return key ? now.getTime() + getTimezoneOffsetMs(now, key) : now.getTime();
}

/**
 * Splits a UTC ISO instant into the wall-clock date/time it represents in
 * `timeZone` — the counterpart to `combineDateTimeInZone`, used to pre-fill
 * date/time form inputs when editing an entry fetched from the API.
 */
export function splitDateTimeInZone(iso: string, timeZone: string): { date: string; time: string } {
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
  const parts: Record<string, string> = {};
  for (const part of dtf.formatToParts(new Date(iso))) {
    if (part.type !== 'literal') parts[part.type] = part.value;
  }
  return {
    date: `${parts.year}-${parts.month}-${parts.day}`,
    time: `${parts.hour}:${parts.minute}`,
  };
}

function getOrdinalSuffix(day: number): string {
  const v = day % 100;
  if (v >= 11 && v <= 13) return 'th';
  switch (day % 10) {
    case 1:
      return 'st';
    case 2:
      return 'nd';
    case 3:
      return 'rd';
    default:
      return 'th';
  }
}

/** Formats an ISO date string as e.g. "24th April 2025", respecting the event's timezone. */
export function formatDateOrdinal(iso?: string, timezone?: string, wallClock = false): string {
  if (!iso) return '—';

  const parsed = wallClock ? UTC_ZONE : timezone ? parseTimezone(timezone) : null;

  let date: Date;
  let tz: string | undefined;

  if (parsed?.type === 'iana') {
    date = new Date(iso);
    tz = parsed.key;
  } else if (parsed?.type === 'offset') {
    date = new Date(shiftByOffset(iso, parsed.minutes));
    tz = 'UTC';
  } else {
    date = new Date(iso);
    tz = undefined;
  }

  const parts = new Intl.DateTimeFormat('en-US', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    ...(tz ? { timeZone: tz } : {}),
  }).formatToParts(date);

  const day = Number(parts.find((p) => p.type === 'day')?.value ?? '0');
  const month = parts.find((p) => p.type === 'month')?.value ?? '';
  const year = parts.find((p) => p.type === 'year')?.value ?? '';

  return `${day}${getOrdinalSuffix(day)} ${month} ${year}`;
}

/** Formats an ISO date string as e.g. "June 29, 2026 — 6:00 AM (GMT-4)". */
export function formatDateTimeLong(iso?: string, timezone?: string, wallClock = false): string {
  if (!iso) return '—';

  const parsed = wallClock ? UTC_ZONE : timezone ? parseTimezone(timezone) : null;
  const tzLabel = timezone ? ` (${timezone})` : '';

  const dateOpts: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  };
  const timeOpts: Intl.DateTimeFormatOptions = {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  };

  let date: Date;
  let tz: string | undefined;

  if (parsed?.type === 'iana') {
    date = new Date(iso);
    tz = parsed.key;
  } else if (parsed?.type === 'offset') {
    date = new Date(shiftByOffset(iso, parsed.minutes));
    tz = 'UTC';
  } else {
    date = new Date(iso);
    tz = undefined;
  }

  const datePart = new Intl.DateTimeFormat('en-US', {
    ...dateOpts,
    ...(tz ? { timeZone: tz } : {}),
  }).format(date);

  const timePart = new Intl.DateTimeFormat('en-US', {
    ...timeOpts,
    ...(tz ? { timeZone: tz } : {}),
  }).format(date);

  return `${datePart} — ${timePart}${tzLabel}`;
}
